import React, { useState } from "react";
import { useLang } from "@/lib/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Mail, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", subject: "", message: "", company: "" };
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || !emailRe.test(form.email) || form.subject.trim().length < 2 || form.message.trim().length < 5) {
      toast.error(t("contact.invalid"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.success"));
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("contact.error"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-[var(--border-light)] bg-black/30 text-white placeholder:text-white/40 font-body text-sm focus:outline-none focus:border-[var(--trust-gold)] focus:ring-2 focus:ring-[var(--trust-gold)]/25 transition-colors";

  return (
    <section id="contact" className="py-20 lg:py-28" style={{ background: "var(--at-deep)" }}>
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-xl mx-auto mb-10">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="font-heading text-xs font-extrabold tracking-[0.25em] uppercase gold-gradient-text">08</span>
            <span className="w-8 h-px bg-[var(--trust-gold)]/50" />
            <span className="font-heading text-[11px] font-bold tracking-[0.2em] uppercase text-white/60">{t("contact.kicker")}</span>
          </div>
          <h2 className="font-serif-display font-bold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">{t("contact.title")}</h2>
          <p className="mt-4 text-base font-body text-white/75 leading-relaxed">{t("contact.subtitle")}</p>
          <a href="mailto:markushuber@austriabet1x2.com" data-testid="contact-email-link" className="mt-4 inline-flex items-center gap-2 font-heading font-bold text-white hover:text-[var(--trust-gold)] transition-colors">
            <Mail size={17} /> markushuber@austriabet1x2.com
          </a>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="at-card-solid rounded-3xl p-8 sm:p-10 grid sm:grid-cols-2 gap-4">
          <input type="text" name="company" value={form.company} onChange={set("company")} tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="contact-company" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
          <div>
            <label className="block text-xs font-bold font-heading uppercase tracking-wide text-white/70 mb-1.5">{t("contact.name")}</label>
            <input data-testid="contact-name" required value={form.name} onChange={set("name")} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-bold font-heading uppercase tracking-wide text-white/70 mb-1.5">{t("contact.email")}</label>
            <input data-testid="contact-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold font-heading uppercase tracking-wide text-white/70 mb-1.5">{t("contact.subject")}</label>
            <input data-testid="contact-subject" required value={form.subject} onChange={set("subject")} className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold font-heading uppercase tracking-wide text-white/70 mb-1.5">{t("contact.message")}</label>
            <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
          </div>
          <button data-testid="contact-submit" type="submit" disabled={loading} className="sm:col-span-2 inline-flex items-center justify-center gap-2 py-3.5 rounded-full text-[#38040C] font-bold font-heading text-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60 shadow-[0_10px_30px_rgba(255,215,0,0.3)]" style={{ background: "linear-gradient(135deg,#FFE566,#FFD700,#D4AF37)" }}>
            {loading ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
            {t("contact.submit")}
          </button>
        </form>
      </div>
    </section>
  );
}
